import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import { useTenant } from '../../contexts/TenantContext';
import TcnAdminLayout from '../../components/tcn-admin/TcnAdminLayout';

/**
 * /tcn/admin/recruitment-bonus — tenant_admin + agency_owner.
 *
 * - Reads the tenant-default config (mode + default paliers, set by super admin).
 * - mode 'fixed'      → read-only (managed by super admin), shown for context.
 * - mode 'per_agency' → editable per-agency paliers, pre-filled from the default:
 *     · agency_owner  → edits their own agency
 *     · tenant_admin  → picks any agency in the tenant and edits it
 */
const TcnAdminRecruitmentBonus = () => {
  const { t } = useTranslation();
  const { tenantId, agencyId, userRole } = useTenant();
  const isTenantAdmin = userRole === 'tenant_admin';

  const [mode, setMode] = useState('fixed');
  const [defaultTiers, setDefaultTiers] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [agencyRow, setAgencyRow] = useState(null); // {id, completion_tiers} or null
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedAt, setSavedAt] = useState(null);

  // 1) Load tenant default + (tenant_admin) the agency list.
  const loadBase = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setError(null);
    const tasks = [
      supabase.from('recruitment_bonus_config').select('mode, completion_tiers').eq('tenant_id', tenantId).is('agency_id', null).maybeSingle(),
    ];
    if (isTenantAdmin) {
      tasks.push(supabase.from('agencies').select('id, name').eq('tenant_id', tenantId).order('name'));
    }
    const [{ data: def, error: defErr }, agRes] = await Promise.all(tasks);
    if (defErr) setError(defErr.message);
    setMode(def?.mode ?? 'fixed');
    setDefaultTiers(def?.completion_tiers ?? []);
    if (isTenantAdmin) {
      const list = (agRes?.data ?? []).filter((a) => a.id !== '00000000-0000-0000-0000-000000000000');
      setAgencies(list);
      setSelectedAgency((prev) => prev ?? agencyId ?? list[0]?.id ?? null);
    } else {
      setSelectedAgency(agencyId ?? null);
    }
    setLoading(false);
  }, [tenantId, agencyId, isTenantAdmin]);

  useEffect(() => { loadBase(); }, [loadBase]);

  // 2) Load the selected agency's row whenever it changes.
  const loadAgencyRow = useCallback(async () => {
    if (!tenantId || !selectedAgency) return;
    const { data } = await supabase
      .from('recruitment_bonus_config')
      .select('id, completion_tiers')
      .eq('tenant_id', tenantId)
      .eq('agency_id', selectedAgency)
      .maybeSingle();
    setAgencyRow(data || null);
    // Pre-fill from tenant default when the agency has no row yet (P0 decision).
    setTiers(data?.completion_tiers?.length ? data.completion_tiers : defaultTiers);
  }, [tenantId, selectedAgency, defaultTiers]);

  useEffect(() => { if (mode === 'per_agency') loadAgencyRow(); }, [loadAgencyRow, mode]);

  const updateTier = (idx, field, value) => {
    const next = [...tiers];
    next[idx] = { ...next[idx], [field]: value === '' ? null : Number(value) };
    setTiers(next);
  };
  const removeTier = (idx) => setTiers(tiers.filter((_, i) => i !== idx));
  const addTier = () => setTiers([...tiers, { pct: 0, amount: 0 }]);
  const sortedTiers = useMemo(
    () => [...tiers].map((tr, i) => ({ ...tr, _origIdx: i })).sort((a, b) => (a.pct ?? 0) - (b.pct ?? 0)),
    [tiers]
  );

  const handleSave = async () => {
    if (!tenantId || !selectedAgency) return;
    setSaving(true);
    setError(null);
    setSavedAt(null);
    const cleanTiers = tiers
      .filter((tr) => tr.pct != null && tr.amount != null)
      .sort((a, b) => Number(a.pct) - Number(b.pct))
      .map((tr) => ({ pct: Number(tr.pct), amount: Number(tr.amount) }));
    const payload = { tenant_id: tenantId, agency_id: selectedAgency, server: 'MENA', mode: 'per_agency', completion_tiers: cleanTiers };
    let dbErr;
    if (agencyRow?.id) {
      ({ error: dbErr } = await supabase.from('recruitment_bonus_config').update(payload).eq('id', agencyRow.id));
    } else {
      ({ error: dbErr } = await supabase.from('recruitment_bonus_config').insert(payload));
    }
    if (dbErr) setError(dbErr.message);
    else setSavedAt(Date.now());
    setSaving(false);
    await loadAgencyRow();
  };

  // FRM-001/TCH-001 — text-base (16px) : pas de zoom iOS au focus.
  const input = 'w-full px-3 py-2 rounded-md bg-card border border-border text-foreground text-base outline-none focus:border-accent focus:ring-1 focus:ring-accent/30';

  return (
    <TcnAdminLayout
      title={t('tcn.admin.recruitment.title', 'Recruitment bonus')}
      subtitle={t('tcn.admin.recruitment.subtitle', 'Completion paliers (recruits ÷ target → flat USD bonus)')}
    >
      {error && <div className="rounded-xl border border-error/30 bg-error/5 px-4 py-2 text-sm text-error mb-4">{error}</div>}

      {loading ? (
        <div className="h-40 rounded-xl skeleton" />
      ) : mode === 'fixed' ? (
        /* Fixed mode → read-only, managed by super admin */
        <div className="rounded-2xl bg-card border border-border p-5 space-y-3">
          <div className="text-sm text-muted-foreground">
            {t('tcn.admin.recruitment.fixedNote', 'Recruitment bonus is fixed for all TCN agencies (set by the platform). These paliers are read-only here.')}
          </div>
          <div className="space-y-2">
            {defaultTiers.length === 0
              ? <div className="text-sm text-muted-foreground">{t('tcn.admin.recruitment.none', 'No paliers configured.')}</div>
              : [...defaultTiers].sort((a, b) => a.pct - b.pct).map((tr, i) => (
                  <div key={i} className="flex justify-between items-center px-4 py-2 rounded-lg bg-background border border-border text-sm">
                    <span className="text-muted-foreground">{tr.pct}% {t('tcn.admin.recruitment.completion', 'completion')}</span>
                    <span className="font-semibold text-foreground tabular-nums">${tr.amount}</span>
                  </div>
                ))}
          </div>
        </div>
      ) : (
        /* Per-agency mode → editable */
        <div className="rounded-2xl bg-card border border-border p-5 space-y-5">
          {isTenantAdmin && (
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">
                {t('tcn.admin.recruitment.agency', 'Agency')}
              </label>
              <select value={selectedAgency || ''} onChange={(e) => setSelectedAgency(e.target.value)} className={`${input} mt-2`}>
                {agencies.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">
                {t('tcn.admin.recruitment.tiers', 'Completion paliers')}
              </label>
              <button type="button" onClick={addTier} className="px-3 py-1 text-xs font-semibold rounded-md bg-primary text-white hover:bg-primary/90">
                + {t('tcn.admin.recruitment.addTier', 'Add palier')}
              </button>
            </div>
            {!agencyRow && (
              <p className="text-xs text-muted-foreground mb-2">
                {t('tcn.admin.recruitment.prefilled', 'Pre-filled from the TCN default — adjust and save to set this agency’s own paliers.')}
              </p>
            )}
            <div className="space-y-2">
              <div className="hidden md:grid grid-cols-[1fr_1fr_auto] gap-3 px-2 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
                <span>{t('tcn.admin.recruitment.pct', 'Completion %')}</span>
                <span>{t('tcn.admin.recruitment.amount', 'Bonus ($)')}</span>
                <span />
              </div>
              {sortedTiers.map((tier) => (
                <div key={tier._origIdx} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 items-center">
                  <input type="number" step="any" value={tier.pct ?? ''} placeholder="50"
                    onChange={(e) => updateTier(tier._origIdx, 'pct', e.target.value)} className={`${input} tabular-nums`} />
                  <input type="number" step="any" value={tier.amount ?? ''} placeholder="100"
                    onChange={(e) => updateTier(tier._origIdx, 'amount', e.target.value)} className={`${input} tabular-nums`} />
                  <button type="button" onClick={() => removeTier(tier._origIdx)} className="px-3 py-2 text-sm rounded-md text-error hover:bg-error/10" aria-label="Remove">✕</button>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between gap-3 pt-2 border-t border-border">
            <div className="text-xs text-muted-foreground">
              {savedAt && t('tcn.admin.recruitment.saved', 'Saved {{time}}', { time: new Date(savedAt).toLocaleTimeString() })}
            </div>
            <button type="button" onClick={handleSave} disabled={saving}
              className="px-4 py-2 rounded-lg bg-accent text-white text-sm font-semibold hover:bg-accent/90 disabled:opacity-50">
              {saving ? t('tcn.admin.recruitment.saving', 'Saving…') : t('tcn.admin.recruitment.save', 'Save')}
            </button>
          </div>
        </div>
      )}
    </TcnAdminLayout>
  );
};

export default TcnAdminRecruitmentBonus;
